/**
 * "Pulse Works" — original trigger-showcase level (difficulty 4/10).
 * Machinery driven entirely by the trigger system: a rising gate wall, a
 * toggle bridge over spikes, color/pulse ambience, camera zoom + shake at a
 * drop, a spawn-chained platform sequence, and a slow-motion spike run.
 */
import type { LevelData, LevelObject } from '../../format/types';
import { UNITS_PER_BLOCK as B } from '../../engine/physics/constants';

let nextId = 1;
const objects: LevelObject[] = [];

function cell(bx: number, by: number): { x: number; y: number } {
  return { x: bx * B + B / 2, y: by * B + B / 2 };
}

function push(o: Omit<LevelObject, 'id'>): void {
  objects.push({ id: nextId++, ...o });
}

const spike = (bx: number, by = 0): void => push({ type: 'spike', ...cell(bx, by) });
const block = (bx: number, by = 0, extra: Partial<LevelObject> = {}): void =>
  push({ type: 'block', ...cell(bx, by), ...extra });
const trig = (
  bx: number,
  props: Record<string, number | string | boolean>,
  extra: Partial<LevelObject> = {},
): void => push({ type: 'trigger', ...cell(bx, 1), props, ...extra });

// --- Act 1: the rising gate (0–60) -------------------------------------------
spike(20);
// A 3-high wall in group 1 blocks the path; a move trigger lifts it in time.
block(34, 0, { groups: [1], colorChannel: 1 });
block(34, 1, { groups: [1], colorChannel: 1 });
block(34, 2, { groups: [1], colorChannel: 1 });
trig(24, { kind: 'move', target: 1, dx: 0, dy: 5, duration: 1.2, easing: 'inout' });
trig(24.4, { kind: 'pulse', channel: 1, hex: '#ffffff', duration: 0.6 });
spike(44);

// --- Act 2: the toggle bridge (60–100) ----------------------------------------
// Spikes line the ground; a hidden bridge (group 2) appears just in time.
for (let bx = 70; bx <= 82; bx++) spike(bx);
for (let bx = 69; bx <= 83; bx += 2) block(bx, 1, { groups: [2], colorChannel: 2 });
trig(2, { kind: 'toggle', target: 2, visible: false }); // hide at level start
trig(58, { kind: 'toggle', target: 2, visible: true }); // reveal before arrival
trig(58.5, { kind: 'pulse', channel: 2, hex: '#a3e635', duration: 0.5 });
spike(90);

// --- Act 3: camera drama (100–150) ----------------------------------------------
trig(100, { kind: 'camera:zoom', zoom: 1.35, duration: 1, easing: 'inout' });
spike(108);
spike(109);
trig(116, { kind: 'camera:shake', strength: 7, duration: 0.5 });
trig(116.5, { kind: 'shader:shockwave', strength: 1, duration: 0.8 });
spike(120);
spike(121);
trig(128, { kind: 'camera:zoom', zoom: 1, duration: 1, easing: 'inout' });
trig(132, { kind: 'color', channel: 3, hex: '#22d3ee', duration: 1.5 });
for (let bx = 134; bx <= 146; bx += 4) spike(bx);

// --- Act 4: spawn-chained steps (150–200) ----------------------------------------
// Three step platforms (groups 11/12/13) rise one after another via a spawn
// chain (3-block spacing keeps each hop within runner jump reach).
block(162, 0, { groups: [11], colorChannel: 3 });
block(165, 0, { groups: [12], colorChannel: 3 });
block(168, 0, { groups: [13], colorChannel: 3 });
trig(150, { kind: 'spawn', target: 20, delay: 0.1 });
trig(
  151,
  { kind: 'move', target: 11, dx: 0, dy: 1, duration: 0.4, easing: 'out', spawnOnly: true },
  { groups: [20] },
);
trig(152, { kind: 'spawn', target: 21, delay: 0.5, spawnOnly: true }, { groups: [20] });
trig(
  153,
  { kind: 'move', target: 12, dx: 0, dy: 2, duration: 0.4, easing: 'out', spawnOnly: true },
  { groups: [21] },
);
trig(154, { kind: 'spawn', target: 22, delay: 0.5, spawnOnly: true }, { groups: [21] });
trig(
  155,
  { kind: 'move', target: 13, dx: 0, dy: 3, duration: 0.4, easing: 'out', spawnOnly: true },
  { groups: [22] },
);
spike(160);
spike(161); // forces using the risen steps
spike(172);
spike(173); // cleared by the drop-off jump from the top step

// --- Act 5: slow-motion finale (200–240) ------------------------------------------
trig(196, { kind: 'timewarp', factor: 0.5 });
trig(197, { kind: 'shader:chromatic', strength: 0.7, duration: 0.6 });
trig(198, { kind: 'camera:rotate', degrees: 5, duration: 1, easing: 'inout' });
spike(206);
spike(207);
spike(214);
spike(215);
trig(222, { kind: 'timewarp', factor: 1 });
trig(222.5, { kind: 'shader:chromatic', strength: 0, duration: 0.6 });
trig(223, { kind: 'camera:rotate', degrees: 0, duration: 1, easing: 'inout' });
spike(228);
// Secret shards along the machinery line.
push({ type: 'collectible', ...cell(36, 1), props: { kind: 'shard' } });
push({ type: 'collectible', ...cell(76, 3), props: { kind: 'shard' } });
push({ type: 'collectible', ...cell(210, 2), props: { kind: 'shard' } });

push({ type: 'endgate', ...cell(240, 2) });

export const PULSE_WORKS: LevelData = {
  formatVersion: 1,
  meta: {
    name: 'Pulse Works',
    author: 'Shape Shifters Team',
    description: 'The machinery is alive. Keep the beat.',
    revision: 1,
    createdAt: '2026-07-23T00:00:00.000Z',
    updatedAt: '2026-07-23T00:00:00.000Z',
    difficulty: 4,
    tags: ['showcase', 'triggers', 'official'],
  },
  song: { id: 'circuit-breaker', bpm: 140, offsetMs: 0 },
  settings: {
    mode: 'normal',
    speed: 'one',
    startForm: 'runner',
    accentColor: '#22d3ee',
    hazardColor: '#f43f5e',
    backgroundColor: '#0d0a24',
  },
  colors: [
    { id: 1, hex: '#fb7185' },
    { id: 2, hex: '#334155' },
    { id: 3, hex: '#a78bfa' },
  ],
  objects,
};
